import { Col, DatePicker, Row, Segmented, Grid, Tooltip } from 'antd'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import locale from 'antd/es/date-picker/locale/az_AZ'
import moment, { Moment } from 'moment'
import { SegmentedLabeledOption } from 'rc-segmented'
import styles from './Segmented.module.scss'
import { DateLimitIcon, ErrorNotificationIco } from '@modules/CustomIcons'
import { useAppSelector } from '@shared/hooks/useAppSelector'

type DateFilterOption = Omit<SegmentedLabeledOption, 'value'> & {
  value:
    | 'none'
    | 'yesterday'
    | 'today'
    | 'thisWeek'
    | 'thisMonth'
    | 'thisYear'
    | 'oneMonth'
    | 'currentQuarter'
    | 'oneYear'
    | 'twoYears'
  dateFrom: Moment
  dateTo: Moment
  label: React.ReactNode
}

interface FilterDateInputsProps {
  value?:
    | 'none'
    | 'today'
    | 'yesterday'
    | 'thisWeek'
    | 'thisMonth'
    | 'thisYear'
    | 'oneMonth'
    | 'currentQuarter'
    | 'oneYear'
    | 'twoYears'
  startDateError?: boolean
  vertical: boolean
  startDateErrorMessage?: string
  endDateError?: boolean
  endDateErrorMessage?: string
  element?: JSX.Element
  onChangePicker: (value: {
    dateFrom?: Moment | null
    dateTo?: Moment | null
    changeType: 'segment' | 'startDate' | 'endDate'
  }) => void
}

function FilterDateInputs(props: FilterDateInputsProps) {
  const {
    value,
    vertical = false,
    onChangePicker,
    startDateError,
    endDateError,
    endDateErrorMessage,
    startDateErrorMessage,
    element,
  } = props
  const { lg, sm, xs } = Grid.useBreakpoint()
  const { t } = useTranslation()
  const dateFormat = 'DD.MM.YYYY'
  const [isDiffSameOrAfter, setDiff] = useState(true)
  const [startDate, setStartDate] = useState<Moment | null>(
    moment(moment().format(dateFormat), dateFormat)
  )
  const [endDate, setEndDate] = useState<Moment>(moment())
  // const [businessDate, setBusinessDate] = useState<Moment>(moment())
  const [activeIndexDate, setDateIndex] = useState(value)

  const businessDate = useAppSelector((state) => state.appConfigs.businessDate)

  const options: DateFilterOption[] = [
    // {
    //   label: t('transfers.today'),
    //   value: 'today',
    //   dateFrom: moment(moment(), dateFormat),
    //   dateTo: moment(businessDate, dateFormat),
    // },
    {
      label: t('transfers.yesterday'),
      value: 'yesterday',
      dateFrom: moment().add(-1, 'days'),
      dateTo: moment().add(-1, 'days'),
    },
    {
      label: t('transfers.thisWeek'),
      value: 'thisWeek',
      dateFrom: moment(moment().startOf('week').add(1, 'days'), dateFormat),
      dateTo: moment(businessDate, dateFormat),
    },
    {
      label: t('transfers.thisMonth'),
      value: 'thisMonth',
      dateFrom: moment(moment().startOf('month'), dateFormat),
      dateTo: moment(businessDate, dateFormat),
    },
    {
      label: t('transfers.oneMonth'),
      value: 'oneMonth',
      dateFrom: moment().subtract(1, 'month'),
      dateTo: moment(businessDate, dateFormat),
    },
    // {
    //   label: t('accountStatement.currentQuarter'),
    //   value: 'currentQuarter',
    //   dateFrom: moment().startOf('quarter'),
    //   dateTo: moment(businessDate, dateFormat),
    //   disabled: true
    // },
    // {
    //   label: t('accountStatement.thisYear'),
    //   value: 'thisYear',
    //   dateFrom: moment().startOf('year'),
    //   dateTo: moment(businessDate, dateFormat),
    //   disabled: true
    // },
    // {
    //   label: t('accountStatement.1year'),
    //   value: 'oneYear',
    //   dateFrom: moment().subtract(1, 'year'),
    //   dateTo: moment(businessDate, dateFormat),
    //   disabled: true
    // },
    // {
    //   label: t('accountStatement.2year'),
    //   value: 'twoYears',
    //   dateFrom: moment().subtract(2, 'year'),
    //   dateTo: moment(businessDate, dateFormat),
    //   disabled: true
    // },
  ]

  const onSegmentedChange = (value: DateFilterOption['value']) => {
    setDateIndex(value)

    const dates = options.find((o) => o.value === value)
    setStartDate(moment(dates?.dateFrom))
    setEndDate(moment(dates?.dateTo))

    onChangePicker({
      dateFrom: dates?.dateFrom,
      dateTo: dates?.dateTo,
      changeType: 'segment',
    })
  }

  const handleStartDate = (value: Moment) => {
    if (value === null) {
      setStartDate(null)
      setDateIndex('none')
      onChangePicker({
        dateFrom: null,
        dateTo: endDate,
        changeType: 'startDate',
      })
    } else {
      setDateIndex('none')
      setStartDate(moment(value, dateFormat))
      onChangePicker({
        dateFrom: value,
        dateTo: endDate,
        changeType: 'startDate',
      })
    }
  }

  const handleEndDate = (value: Moment) => {
    if (value === null) {
      setEndDate(null)
      setDateIndex('none')
      onChangePicker({
        dateTo: null,
        dateFrom: startDate,
        changeType: 'endDate',
      })
    } else {
      setEndDate(moment(value, dateFormat))
      setDateIndex('none')
      onChangePicker({
        dateTo: value,
        dateFrom: startDate,
        changeType: 'endDate',
      })
    }
  }

  useEffect(() => {
    const dates = options.find((o) => o.value === activeIndexDate)
    setStartDate(moment(dates?.dateFrom, dateFormat))
    setEndDate(moment(dates?.dateTo, dateFormat))
    onChangePicker({
      dateTo: dates?.dateTo,
      dateFrom: dates?.dateFrom,
      changeType: 'segment',
    })
  }, [])

  return (
    <Row
      className={styles.datepicker}
      justify="start"
      style={{ gap: 20, flexDirection: vertical ? 'column' : 'row' }}
    >
      <Col>
        <Row style={{ gap: 20, flexDirection: lg ? 'row' : 'column' }}>
          <Col>
            <Row
              justify="start"
              align={xs ? 'stretch' : 'middle'}
              style={{ gap: 20, flexDirection: xs ? 'column' : 'row' }}
            >
              <Col>{t('transfers.period')}</Col>
              <Col>
                <DatePicker
                  name="startDate"
                  style={{ width: '100%' }}
                  locale={locale}
                  status={startDateError ? 'error' : ''}
                  onChange={handleStartDate}
                  value={startDate ? moment(startDate, dateFormat) : undefined}
                  format={dateFormat}
                  allowClear={false}
                />
                {startDateError && (
                  <Tooltip
                    style={{
                      position: 'absolute',
                      right: -50,
                      cursor: 'pointer',
                    }}
                    color="#f3646c"
                    title={t(startDateErrorMessage) ?? ''}
                  >
                    <div className={styles.errorTooltipIcon}>
                      <ErrorNotificationIco />
                    </div>
                  </Tooltip>
                )}
              </Col>
              {sm && <Col>-</Col>}
              <Col style={{ position: 'relative' }}>
                <DatePicker
                  name="endDate"
                  style={{ width: '100%' }}
                  status={endDateError ? 'error' : ''}
                  locale={locale}
                  className={!isDiffSameOrAfter ? 'errored-ant-picker' : ''}
                  onChange={handleEndDate}
                  value={endDate ? moment(endDate, dateFormat) : undefined}
                  format={dateFormat}
                  allowClear={false}
                />
                {endDateError && (
                  <Tooltip
                    style={{
                      position: 'absolute',
                      right: -50,
                      cursor: 'pointer',
                    }}
                    color="#f3646c"
                    title={t(endDateErrorMessage) ?? ''}
                  >
                    <div className={styles.errorTooltipIcon}>
                      <ErrorNotificationIco />
                    </div>
                  </Tooltip>
                )}
              </Col>
              {
                sm && <Col>
                <Tooltip
                  placement={xs ? 'right' : 'bottom'}
                  title={t('acquiring.note')}
                >
                  <Row
                    style={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: '#D9E8F7',
                      borderRadius: 8,
                    }}
                  >
                    <DateLimitIcon />
                  </Row>
                </Tooltip>
              </Col>
              }
            </Row>
          </Col>

          {element && element}
        </Row>
      </Col>
      <Col>
        <Segmented
          className={styles.customSegmented}
          options={options}
          value={activeIndexDate}
          onChange={onSegmentedChange}
        />
      </Col>
    </Row>
  )
}

export default FilterDateInputs
